import React, { useState, useMemo } from "react";
import { IconButton } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "../loginPage.css";
import { resetPasswordApi } from "../api/changeAndResetPasswordApi";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../utils/apiError";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const isPasswordValid = useMemo(() => {
    return PASSWORD_REGEX.test(password);
  }, [password]);

  const isConfirmPasswordValid =
    password === confirmPassword && confirmPassword.length > 0;

  const isFormValid = useMemo(() => {
    return isPasswordValid && isConfirmPasswordValid;
  }, [isPasswordValid, isConfirmPasswordValid]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError(
        "Password must be at least 8 characters, include uppercase, lowercase, number, and special character"
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const payload = {
        token,
        new_password: password,
      };

      const res = await resetPasswordApi(payload);

      if (res?.status === true) {
        toast.success(res?.message || "Password updated successfully");

        setTimeout(() => {
          navigate("/");
        }, 1500);

        return;
      }

      const failMsg = res?.message || "Password update failed";
      toast.error(failMsg);
      setError(failMsg);
    } catch (error) {
      const msg = getApiErrorMessage(error, "Failed to update password");
      toast.error(msg);
      setError(msg);
    }
  }; 

  return (
    <div className="login-container">    
      <h2>Update Password</h2>
      <div className="input-group">
        <label>New Password</label>
        <div className="password-input-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <IconButton
            onClick={() => setShowPassword((prev) => !prev)}
            size="small"
            disableRipple
          >
            {showPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </div>
      </div>

      <div className="input-group">
        <label>Confirm Password</label>
        <div className="password-input-wrapper">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <IconButton
            onClick={() => setShowConfirmPassword((prev) => !prev)}
            size="small"
            disableRipple
          >
            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </div>
      </div>

      {password && !PASSWORD_REGEX.test(password) && (
        <p className="error-message">
          Password must be at least 8 characters and include uppercase,
          lowercase, number, and special character.
        </p>
      )}

      {confirmPassword && password !== confirmPassword && (
        <p className="error-message">Passwords do not match.</p>
      )}

      {error && <p className="error-message">{error}</p>}

      <button
        className="login-button"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        Update Password
      </button>
    </div>
  );
};

export default UpdatePassword;